import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBuilding,
  faEnvelope,
  faFileArrowUp,
  faPaperPlane,
  faPhone,
  faRotate,
  faSpinner,
  faTicket,
  faTrash,
  faUserTie,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../../components/Ui_Element/DotLoader";
import {
  createAdminSupportRequest,
  fetchAdminTicketCustomerUsers,
  fetchAdminTicketCustomers,
  fetchAdminTicketEmployees,
} from "./adminTicketService";
import { getCurrentUserIdentity, getIdentityRoleLabel, isCustomerIdentity } from "../ticketUtils";

const emptyForm = {
  employeeUserId: "",
  customerId: 0,
  customerUserId: "",
  subject: "",
  domainUrl: "",
  category: "Technical",
  priority: "Medium",
  contactEmail: "",
  description: "",
  attachmentFile: null,
};

const canManageAllTickets = (identity) =>
  (identity?.normalizedRoles || []).some((role) => {
    return role.includes("admin") || role.includes("support") || role.includes("help") || role.includes("manager") || role.includes("hr");
  });

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label} {required ? <span className="text-rose-500">*</span> : null}
    </label>
    {children}
    {error ? <div className="mt-2 text-xs font-medium text-rose-600">{error}</div> : null}
  </div>
);

const StatCard = ({ icon, label, value, helper }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-medium text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
        <div className="mt-1 text-xs text-slate-500">{helper}</div>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
        <FontAwesomeIcon icon={icon} />
      </div>
    </div>
  </div>
);

export default function AdminTicketsPage() {
  const navigate = useNavigate();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const allowAccess = useMemo(() => !isCustomerIdentity(identity) && canManageAllTickets(identity), [identity]);

  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [formState, setFormState] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [customerUserLoading, setCustomerUserLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onDropAttachment = React.useCallback((acceptedFiles) => {
    const nextFile = acceptedFiles?.[0] || null;
    setFormState((current) => ({
      ...current,
      attachmentFile: nextFile,
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: onDropAttachment,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    if (!allowAccess) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const [employeeItems, customerItems] = await Promise.all([
          fetchAdminTicketEmployees(),
          fetchAdminTicketCustomers(),
        ]);

        if (cancelled) return;
        setEmployees(employeeItems);
        setCustomers(customerItems);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.message || "Ticket request setup data could not be loaded.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [allowAccess]);

  const employeeOptions = useMemo(
    () =>
      employees.map((item) => ({
        ...item,
        label: `${item.fullName || "Employee"}${item.employeeCode ? ` (${item.employeeCode})` : item.employeeNumber ? ` (${item.employeeNumber})` : ""}`,
      })),
    [employees]
  );

  const filteredCustomers = useMemo(() => {
    if (!formState.employeeUserId) return [];
    return customers
      .filter((item) => item.empUserId && item.empUserId === formState.employeeUserId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [customers, formState.employeeUserId]);

  const selectedEmployee = useMemo(
    () => employeeOptions.find((item) => item.userId === formState.employeeUserId) || null,
    [employeeOptions, formState.employeeUserId]
  );

  const selectedCustomer = useMemo(
    () => filteredCustomers.find((item) => item.id === Number(formState.customerId || 0)) || null,
    [filteredCustomers, formState.customerId]
  );

  const selectedCustomerUser = useMemo(
    () => customerUsers.find((item) => item.userId === formState.customerUserId) || null,
    [customerUsers, formState.customerUserId]
  );

  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerUsers([]);
      setFormState((current) => ({
        ...current,
        customerUserId: "",
        contactEmail: current.customerUserId ? selectedCustomer?.email || "" : current.contactEmail,
      }));
      return;
    }

    let cancelled = false;

    const loadCustomerUsers = async () => {
      setCustomerUserLoading(true);
      setError("");

      try {
        const items = await fetchAdminTicketCustomerUsers(selectedCustomer.id);
        if (cancelled) return;

        setCustomerUsers(items);
        setFormState((current) => ({
          ...current,
          customerUserId: "",
          domainUrl: current.domainUrl || selectedCustomer.domain || "",
          contactEmail: current.contactEmail || selectedCustomer.email || "",
        }));
      } catch (requestError) {
        if (!cancelled) {
          setCustomerUsers([]);
          setError(requestError?.message || "Customer users could not be loaded.");
        }
      } finally {
        if (!cancelled) setCustomerUserLoading(false);
      }
    };

    loadCustomerUsers();

    return () => {
      cancelled = true;
    };
  }, [selectedCustomer]);

  useEffect(() => {
    if (!selectedCustomerUser) {
      if (selectedCustomer) {
        setFormState((current) => ({
          ...current,
          contactEmail: current.contactEmail || selectedCustomer.email || "",
        }));
      }
      return;
    }

    setFormState((current) => ({
      ...current,
      contactEmail: selectedCustomerUser.email || current.contactEmail,
    }));
  }, [selectedCustomer, selectedCustomerUser]);

  const validateForm = () => {
    const nextErrors = {};

    if (!formState.employeeUserId.trim()) nextErrors.employeeUserId = "Employee is required.";
    if (!Number(formState.customerId || 0)) nextErrors.customerId = "Customer is required.";
    if (!formState.subject.trim()) nextErrors.subject = "Subject is required.";
    if (!formState.category) nextErrors.category = "Category is required.";
    if (!formState.priority) nextErrors.priority = "Priority is required.";
    if (!formState.contactEmail.trim()) nextErrors.contactEmail = "Contact email is required.";
    else if (!/\S+@\S+\.\S+/.test(formState.contactEmail.trim())) nextErrors.contactEmail = "Contact email is invalid.";
    if (!formState.description.trim()) nextErrors.description = "Description is required.";

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError("");

    try {
      const response = await createAdminSupportRequest(formState);
      if (response?.status === 200) {
        navigate(`/tickets/${response.data.id}`, { state: { backTo: "/tickets/admin/new-request" } });
        return;
      }

      setError(response?.message || "Support request could not be created.");
    } catch {
      setError("Support request could not be created.");
    } finally {
      setSaving(false);
    }
  };

  if (!allowAccess) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-6">
        <Breadcrumb pageName="Open New Support Request" currentPage="Open New Support Request" />
        <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
          This page is available only to support admins and managers who can create tickets on behalf of customers.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Open New Support Request" currentPage="Open New Support Request" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_28%),linear-gradient(135deg,#0f172a,#162133_58%,#243b53)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute bottom-0 right-0 h-40 w-40 translate-x-12 translate-y-10 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faTicket} />
                Admin Ticket Desk
              </div>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight md:text-3xl">Open a support request for a customer</h1>
              <p className="mt-3 text-sm text-slate-200 md:text-base">
                Choose the assigned employee, narrow to that employee&apos;s customers, then create the ticket using either the customer account or a specific customer user.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identityRoleLabel}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.email || "No email found"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/tickets")}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Tickets
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-200 bg-slate-50/80 p-5 md:grid-cols-3 md:p-6">
          <StatCard icon={faUserTie} label="Employees" value={employees.length} helper="Selectable support owners" />
          <StatCard icon={faBuilding} label="Customers" value={filteredCustomers.length} helper="Filtered by selected employee" />
          <StatCard icon={faUsers} label="Customer Users" value={customerUsers.length} helper="Optional requester override" />
        </div>

        <div className="p-5 md:p-6">
          {error ? (
            <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <div className="mb-3 flex justify-center">
                <DotLoader color="#0f172a" size="w-1.5 h-1.5" />
              </div>
              Loading employee and customer data...
            </div>
          ) : (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Employee" required error={formErrors.employeeUserId}>
                    <select
                      value={formState.employeeUserId}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          employeeUserId: event.target.value,
                          customerId: 0,
                          customerUserId: "",
                          contactEmail: "",
                          domainUrl: "",
                        }))
                      }
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    >
                      <option value="">Select employee</option>
                      {employeeOptions.map((item) => (
                        <option key={item.userId || item.id} value={item.userId}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Customer" required error={formErrors.customerId}>
                    <select
                      value={formState.customerId}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          customerId: Number(event.target.value || 0),
                          customerUserId: "",
                          contactEmail: "",
                          domainUrl: "",
                        }))
                      }
                      disabled={!formState.employeeUserId}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                    >
                      <option value="0">{formState.employeeUserId ? "Select customer" : "Select employee first"}</option>
                      {filteredCustomers.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}{item.customerCode ? ` (${item.customerCode})` : ""}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Customer User" error={formErrors.customerUserId}>
                    <select
                      value={formState.customerUserId}
                      onChange={(event) => setFormState((current) => ({ ...current, customerUserId: event.target.value }))}
                      disabled={!selectedCustomer || customerUserLoading}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                    >
                      <option value="">
                        {customerUserLoading ? "Loading customer users..." : "Use customer default account"}
                      </option>
                      {customerUsers.map((item) => (
                        <option key={item.userId || item.id} value={item.userId}>
                          {item.fullName || item.email || item.userId}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Contact Email" required error={formErrors.contactEmail}>
                    <input
                      type="email"
                      value={formState.contactEmail}
                      onChange={(event) => setFormState((current) => ({ ...current, contactEmail: event.target.value }))}
                      placeholder="requester@example.com"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    />
                  </Field>

                  <Field label="Subject" required error={formErrors.subject}>
                    <input
                      type="text"
                      value={formState.subject}
                      onChange={(event) => setFormState((current) => ({ ...current, subject: event.target.value }))}
                      placeholder="Short summary of the support request"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    />
                  </Field>

                  <Field label="Domain URL">
                    <input
                      type="text"
                      value={formState.domainUrl}
                      onChange={(event) => setFormState((current) => ({ ...current, domainUrl: event.target.value }))}
                      placeholder="https://example.com"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    />
                  </Field>

                  <Field label="Category" required error={formErrors.category}>
                    <select
                      value={formState.category}
                      onChange={(event) => setFormState((current) => ({ ...current, category: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    >
                      <option value="Billing">Billing</option>
                      <option value="Technical">Technical</option>
                      <option value="General">General</option>
                    </select>
                  </Field>

                  <Field label="Priority" required error={formErrors.priority}>
                    <select
                      value={formState.priority}
                      onChange={(event) => setFormState((current) => ({ ...current, priority: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </Field>

                  <div className="md:col-span-2">
                    <Field label="Description" required error={formErrors.description}>
                      <textarea
                        rows={7}
                        value={formState.description}
                        onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
                        placeholder="Describe the issue reported by phone call or email."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                      />
                    </Field>
                  </div>

                  <div className="md:col-span-2">
                    <Field label="Attachment">
                      <div
                        {...getRootProps()}
                        className={`rounded-2xl border-2 border-dashed px-5 py-6 transition ${
                          isDragActive
                            ? "border-slate-900 bg-slate-100"
                            : "border-slate-300 bg-slate-50 hover:border-slate-400"
                        }`}
                      >
                        <input {...getInputProps()} />
                        <div className="flex flex-col gap-3 text-center sm:text-left">
                          <div className="flex items-center justify-center sm:justify-start">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
                              <FontAwesomeIcon icon={faFileArrowUp} />
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {isDragActive ? "Drop the file here" : "Drag and drop an attachment here"}
                            </div>
                            <div className="mt-1 text-sm text-slate-500">
                              Or choose a file manually if you prefer.
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                            <button
                              type="button"
                              onClick={open}
                              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                              <FontAwesomeIcon icon={faFileArrowUp} />
                              Choose File
                            </button>
                            {formState.attachmentFile ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setFormState((current) => ({
                                    ...current,
                                    attachmentFile: null,
                                  }))
                                }
                                className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                Remove
                              </button>
                            ) : null}
                          </div>
                          {formState.attachmentFile ? (
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                              Selected file: <span className="font-semibold">{formState.attachmentFile.name}</span>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400">No file selected</div>
                          )}
                        </div>
                      </div>
                    </Field>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : faPaperPlane} spin={saving} />
                    {saving ? "Creating..." : "Create Support Request"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormState(emptyForm)}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900">Requester Preview</h2>
                  <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Employee</div>
                      <div className="mt-1 text-slate-900">{selectedEmployee?.label || "Not selected"}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Customer</div>
                      <div className="mt-1 text-slate-900">{selectedCustomer?.name || "Not selected"}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Customer User</div>
                      <div className="mt-1 text-slate-900">{selectedCustomerUser?.fullName || "Customer default account will be used"}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Effective Requester</div>
                      <div className="mt-1 text-slate-900">
                        {selectedCustomerUser?.userId || selectedCustomer?.userId || "No requester resolved yet"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Contact</div>
                      <div className="mt-1 flex items-center gap-2 text-slate-900">
                        <FontAwesomeIcon icon={faEnvelope} className="text-slate-400" />
                        <span>{formState.contactEmail || "No email selected"}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-slate-500">
                        <FontAwesomeIcon icon={faPhone} className="text-slate-400" />
                        <span>{selectedCustomerUser?.mobile || selectedCustomer?.mobile || "No phone found"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <h2 className="text-lg font-semibold text-slate-900">Ticket Logic</h2>
                  <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <p>If no customer user is selected, the ticket will use the customer account user ID.</p>
                    <p>If a customer user is selected, the ticket will use that customer user&apos;s user ID.</p>
                    <p>The created ticket is marked as an admin-created request so it can be tracked separately in the backend.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
